/**
 * Box 7 of the ICS-201: initial response objectives, current actions and
 * planned actions.
 *
 * Rather than a third objectives-and-actions editor, this is the pair the other
 * cards already print from: the ICS-202 objectives table and the ICS-233
 * actions workspace, over the same incident-scoped records. Planned and
 * current/ongoing are the action's own status, so both live in one table and
 * the Status column tells them apart, which is how the 233 prints them.
 *
 * Neither is scoped to an operational period, so both work during initial
 * response, and neither records a version against its own base id, so nothing
 * here writes to the 202's or the 233's history.
 */

import React, { useState } from 'react';
import { Plus } from 'lucide-react';

import { Button } from '@/components/ui/button';
import ActionDrawer from 'components/IAP/ActionDrawer';
import ActionsWorkspace from 'components/IAP/ActionsWorkspace';
import IncidentObjectivesTable from 'components/IAP/IncidentObjectivesTable';
import { INCIDENT } from 'components/IAP/ICSConstants';
import {
  openInstantiatedIncidentTask,
  useInstantiateIncidentTask,
} from 'components/IAP/hooks/incidentActionHooks';
import type { ActionEntity } from 'components/IAP/hooks/useScopedActionForm';
import { useHwcgFlag } from 'utils/featureFlags';

import { ICS204_TABLE } from '../AssignmentList/assignmentCardStyles';

export const Ics201ObjectivesActionsBox: React.FC<{ readOnly?: boolean }> = ({
  readOnly = false,
}) => {
  const hwcg = useHwcgFlag();
  const { instantiate, isPending: creatingTask } = useInstantiateIncidentTask();
  const [showAddDrawer, setShowAddDrawer] = useState(false);
  const [selectedAction, setSelectedAction] = useState<ActionEntity | null>(
    null
  );

  const canEdit = !readOnly;

  const addAction = () => {
    if (!hwcg) {
      setShowAddDrawer(true);
      return;
    }
    if (creatingTask) return;
    void openInstantiatedIncidentTask({
      instantiate,
      status: 'Planned',
      onOpen: setSelectedAction,
    });
  };

  return (
    <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-5">
      <div data-section="objectives">
        <IncidentObjectivesTable readOnly={readOnly} className={ICS204_TABLE} />
      </div>

      <div data-section="actions" data-tour="ics201-actions">
        {/* A storage key of its own, so the card's column and view choices do not move the ICS-233's. */}
        <ActionsWorkspace
          scope={INCIDENT}
          storageKey="ics201-actions-view"
          canEdit={canEdit}
          onEditAction={setSelectedAction}
          toolbar={
            canEdit ? (
              <Button
                type="button"
                size="sm"
                disabled={creatingTask}
                onClick={addAction}
                data-tour="ics201-add-action"
              >
                <Plus className="tw:size-4" />
                Add Action
              </Button>
            ) : null
          }
        />
      </div>

      {canEdit && !hwcg && showAddDrawer ? (
        <ActionDrawer scope={INCIDENT} toggle={() => setShowAddDrawer(false)} />
      ) : null}
      {canEdit && selectedAction ? (
        <ActionDrawer
          scope={INCIDENT}
          selectedEntity={selectedAction}
          toggle={() => setSelectedAction(null)}
        />
      ) : null}
    </div>
  );
};

export default Ics201ObjectivesActionsBox;
